import { useCallback, useMemo } from 'react';
import { useIsOrgAdmin } from '@atlassian/jira-business-goal-picker/src/services/is-org-admin/index.tsx';
import {
	NOT_ENROLLED,
	VARIATION,
} from '@atlassian/jira-common-util-get-experiment-group/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { FREE_EDITION } from '@atlassian/jira-shared-types/src/edition.tsx';
import { useAppEditions } from '@atlassian/jira-tenant-context-controller/src/components/app-editions/index.tsx';
import { useIsSiteAdmin } from '@atlassian/jira-tenant-context-controller/src/components/is-site-admin/index.tsx';
import {
	ISSUE_VIEW_END_USERS_CROSS_FLOW_VIA_E2E_ANALYTICS_KEY,
	ISSUE_VIEW_END_USERS_CROSS_FLOW_VIA_E2E_EXPERIMENT_KEY,
} from '../constants.tsx';

/**
 * @see https://switcheroo.atlassian.com/ui/gates/8a98845f-bd2b-4b73-a9c0-7f62841f5fd1
 * @see https://switcheroo.atlassian.com/ui/gates/e274ebf1-9985-4a34-8872-5c40d1ce933c/key/end_user_xflow_via_e2e_killswitch
 * @see https://console.statsig.com/LqivKg6ADZZaGczRfBKfX/experiments/issue_view_end_users_cross-flow_via_e2e_flow/setup
 */
export const useIssueViewEndUsersCrossFlowViaE2EExperiment = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isSiteAdmin = useIsSiteAdmin();
	const { isOrgAdmin } = useIsOrgAdmin();
	const appEditions = useAppEditions();

	/**
	 * Returns true when the kill switch is enabled, the tenant is on a free edition, and a user is not an admin (end user).
	 * This determines if any other experiment code is executed.
	 */
	const experimentIsEnabled = useMemo(() => {
		if (!fg('end_user_xflow_via_e2e_killswitch')) {
			return false;
		}

		if (appEditions.software !== FREE_EDITION) {
			return false;
		}

		if (isSiteAdmin || isOrgAdmin) {
			return false;
		}

		return true;
	}, [appEditions, isSiteAdmin, isOrgAdmin]);

	/**
	 * Determine the experiment cohort. If the experimentIsEnabled is false, this always returns NOT_ENROLLED.
	 */
	const getExperimentCohort = useCallback(() => {
		if (experimentIsEnabled) {
			return expVal('issue_view_end_users_cross-flow_via_e2e_flow', 'cohort', 'not-enrolled');
		}

		return NOT_ENROLLED;
	}, [experimentIsEnabled]);

	/**
	 * Determine if the user is enrolled in the experiment. If the experimentIsEnabled is false, this always returns false
	 */
	const isEnrolledInExperiment = useCallback(() => {
		if (experimentIsEnabled) {
			return getExperimentCohort() !== NOT_ENROLLED;
		}

		return false;
	}, [experimentIsEnabled, getExperimentCohort]);

	/**
	 * Conditionally fire the exposure event. If the user is not enrolled in this experiment, this is a no-op.
	 */
	const fireExposureEvent = useCallback(() => {
		if (isEnrolledInExperiment()) {
			const cohort = getExperimentCohort();

			fireTrackAnalytics(
				createAnalyticsEvent({}),
				`${ISSUE_VIEW_END_USERS_CROSS_FLOW_VIA_E2E_ANALYTICS_KEY} exposed`,
				{
					experiment: ISSUE_VIEW_END_USERS_CROSS_FLOW_VIA_E2E_EXPERIMENT_KEY,
					cohort,
				},
			);
		}
	}, [isEnrolledInExperiment, createAnalyticsEvent, getExperimentCohort]);

	/**
	 * Returns true when the cohort is variation.
	 */
	const isVariation = useCallback(() => getExperimentCohort() === VARIATION, [getExperimentCohort]);

	return {
		experimentIsEnabled,
		isEnrolledInExperiment,
		fireExposureEvent,
		getExperimentCohort,
		isVariation,
	};
};
